import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle, Dot, Pause } from "lucide-react";

const LABELS = {
  coursework: "Coursework", comps: "Comprehensive / General Exams",
  topic_development: "Dissertation Topic Development", proposal: "Proposal Development",
  proposal_defense: "Proposal Defense", irb: "IRB / Ethics Approval",
  research: "Research / Data Collection", analysis: "Data Analysis",
  dissertation: "Dissertation Writing", final_review: "Final Dissertation Review",
  defense: "Dissertation Defense", revisions: "Revisions",
  graduation: "Graduation", publishing: "Publishing / Career Development",
};
const STATUSES = ["NOT_STARTED", "IN_PROGRESS", "COMPLETED", "ON_HOLD"];
const STATUS_LABELS = { NOT_STARTED: "Not started", IN_PROGRESS: "In progress", COMPLETED: "Complete", ON_HOLD: "On hold" };

export default function Journey() {
  const [stages, setStages] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, in_progress: 0, percent: 0 });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "NOT_STARTED", target_date: "", completion_date: "", private_notes: "" });

  const load = useCallback(async () => {
    const r = await api.get("/journey/milestones");
    setStages(r.data.stages);
    setMilestones(r.data.milestones);
    if (r.data.progress) setProgress(r.data.progress);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (m) => {
    setEditing(m.stage);
    setForm({
      status: m.status || "NOT_STARTED",
      target_date: (m.target_date || "").slice(0, 10),
      completion_date: (m.completion_date || "").slice(0, 10),
      private_notes: m.private_notes || "",
    });
  };
  const save = async () => {
    try {
      await api.put("/journey/milestones", { stage: editing, ...form });
      toast.success("Milestone updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const iconFor = (s) => s === "COMPLETED" ? CheckCircle2 : s === "IN_PROGRESS" ? Dot : s === "ON_HOLD" ? Pause : Circle;

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="journey-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Your journey · private</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">The long, quiet marathon.</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">Track each stage privately. Only you see your milestones.</p>

      <Card className="mt-8 p-5 lg:p-6" data-testid="journey-progress-card">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Overall progress</p>
            <p className="font-serif text-3xl font-black tracking-tight mt-1" data-testid="journey-progress-percent">{progress.percent}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground" data-testid="journey-progress-counts">
              <span className="text-foreground font-medium">{progress.completed}</span> of {progress.total} stages complete
              {progress.in_progress > 0 && <> · {progress.in_progress} in progress</>}
            </p>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden border border-border">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progress.percent}%` }}
            data-testid="journey-progress-bar"
          />
        </div>
      </Card>

      <ol className="mt-12 relative border-l border-border pl-8" data-testid="journey-timeline">
        {stages.map((s, i) => {
          const m = milestones.find((x) => x.stage === s) || { stage: s, status: "NOT_STARTED" };
          const Icon = iconFor(m.status);
          const isCurrent = m.status === "IN_PROGRESS";
          const isDone = m.status === "COMPLETED";
          return (
            <li key={s} className="mb-8 last:mb-0" data-testid={`stage-row-${s}`}>
              <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${
                isCurrent ? "bg-primary text-primary-foreground" :
                isDone ? "bg-secondary text-primary" :
                m.status === "ON_HOLD" ? "bg-secondary text-muted-foreground" :
                "bg-card border border-border text-muted-foreground"}`}>
                <Icon className="h-4 w-4" />
              </span>
              <Card className={`p-4 lg:p-5 ${isCurrent ? "border-primary" : ""}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Stage {String(i + 1).padStart(2, "0")}</span>
                      {isCurrent && <span className="text-[10px] uppercase tracking-widest text-primary">Current</span>}
                    </div>
                    <h3 className="font-serif text-xl font-black tracking-tight mt-1">{LABELS[s] || s}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Status: <span className="text-foreground font-medium">{STATUS_LABELS[m.status]}</span>
                      {m.target_date && <> · Target: {m.target_date.slice(0, 10)}</>}
                      {m.completion_date && <> · Completed: {m.completion_date.slice(0, 10)}</>}
                    </p>
                  </div>
                  <Button size="sm" variant={editing === s ? "default" : "outline"} className="rounded-full" onClick={() => editing === s ? setEditing(null) : openEdit(m)} data-testid={`edit-stage-${s}`}>
                    {editing === s ? "Cancel" : "Update"}
                  </Button>
                </div>
                {editing === s && (
                  <div className="mt-4 pt-4 border-t border-border grid md:grid-cols-2 gap-4" data-testid={`edit-form-${s}`}>
                    <div><Label>Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid={`status-${s}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{STATUS_LABELS[st]}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Target date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid={`target-${s}`} /></div>
                    <div><Label>Completion date</Label><Input type="date" value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} data-testid={`completion-${s}`} /></div>
                    <div className="md:col-span-2"><Label>Private notes</Label><Textarea value={form.private_notes} onChange={(e) => setForm({ ...form, private_notes: e.target.value })} className="min-h-[60px]" data-testid={`notes-${s}`} /></div>
                    <div className="md:col-span-2 flex justify-end"><Button onClick={save} className="rounded-full" data-testid={`save-${s}`}>Save milestone</Button></div>
                  </div>
                )}
              </Card>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
